import React, { useState, useEffect } from 'react';
import CpcTrafficClientsForm from './CpcTrafficClientsForm';
import CpcTrafficClientsList from './CpcTrafficClientsList';
import CpcTrafficClientsHistory from './CpcTrafficClientsHistory';
import CpcTrafficClientsQuickActionModal from './CpcTrafficClientsQuickActionModal';
import { loadCpcTrafficClients } from '../api';

const CpcTrafficClientsSection = ({ currentUser }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showQuickAction, setShowQuickAction] = useState(false);
  const [quickActionRecord, setQuickActionRecord] = useState(null);
  const [activeTab, setActiveTab] = useState('todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');

  useEffect(() => {
    loadRecords();
  }, []);

  const loadRecords = async () => {
    try {
      setLoading(true);
      
      // Pasar las células del usuario si requiere filtrado por células
      const userCells = currentUser && currentUser.role !== 'admin' && currentUser.cells ? currentUser.cells : null;
      const userRole = currentUser ? currentUser.role : null;
      const userEmail = currentUser ? currentUser.email : null;
      console.log('🔍 CPC Traffic ClientsSection - Cargando con células del usuario:', userCells);
      console.log('🔍 CPC Traffic ClientsSection - Rol del usuario:', userRole);
      console.log('🔍 CPC Traffic ClientsSection - Email del usuario:', userEmail);
      
      const recordsData = await loadCpcTrafficClients(userCells, userRole, userEmail);
      setRecords(recordsData || []);
    } catch (error) {
      console.error('Error loading cpc traffic clients:', error);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (recordData) => {
    try {
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      const url = editingRecord 
        ? `${API_URL}/cpc-traffic-clients/${editingRecord.id}`
        : `${API_URL}/cpc-traffic-clients`;
      
      const method = editingRecord ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...recordData,
          user_name: currentUser?.name || currentUser?.email || 'Sistema'
        }),
      });

      if (response.ok) {
        alert(editingRecord 
          ? 'Registro actualizado exitosamente' 
          : 'Registro creado exitosamente');
        setShowForm(false);
        setEditingRecord(null);
        loadRecords();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al guardar el registro');
      }
    } catch (error) {
      console.error('Error al guardar:', error);
      alert(`Error al guardar: ${error.message}`);
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setShowForm(true);
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este registro?')) {
      return;
    }

    try {
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      const response = await fetch(`${API_URL}/cpc-traffic-clients/${recordId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        alert('Registro eliminado exitosamente');
        loadRecords();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al eliminar el registro');
      }
    } catch (error) {
      console.error('Error al eliminar:', error);
      alert(`Error al eliminar: ${error.message}`);
    }
  };

  const handleShowHistory = (record) => {
    setSelectedRecord(record);
    setShowHistory(true);
  };

  const handleQuickAction = (record) => {
    setQuickActionRecord(record);
    setShowQuickAction(true);
  };

  const handleQuickActionSave = async (recordId, actionData) => {
    try {
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      if (actionData.action_type === 'status_changed') {
        const currentRecord = records.find(r => r.id === recordId);
        if (!currentRecord) {
          throw new Error('Registro no encontrado');
        }

        const updateData = {
          mes: currentRecord.mes,
          seguimiento: currentRecord.seguimiento,
          fecha_asunto: currentRecord.fecha_asunto ? new Date(currentRecord.fecha_asunto).toISOString().split('T')[0] : null,
          nombre_cliente: currentRecord.nombre_cliente,
          cuenta_publicitaria: currentRecord.cuenta_publicitaria,
          celula: currentRecord.celula,
          campana: currentRecord.campana,
          asunto: currentRecord.asunto,
          acciones: currentRecord.acciones,
          status: actionData.new_status
        };

        const updateResponse = await fetch(`${API_URL}/cpc-traffic-clients/${recordId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData),
        });

        if (!updateResponse.ok) {
          const errorData = await updateResponse.json();
          throw new Error(errorData.error || 'Error al actualizar el estado');
        }
      }

      const response = await fetch(`${API_URL}/cpc-traffic-clients/${recordId}/history`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(actionData),
      });

      if (response.ok) {
        alert('Acción registrada exitosamente');
        setShowQuickAction(false);
        setQuickActionRecord(null);
        loadRecords();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al registrar la acción');
      }
    } catch (error) {
      console.error('Error al registrar acción:', error);
      throw error;
    }
  };

  const getFilteredRecords = () => {
    let filtered = records;

    if (activeTab === 'pendientes') {
      filtered = filtered.filter(record => record.status === 'pendiente');
    } else if (activeTab === 'completados') {
      filtered = filtered.filter(record => record.status === 'completado');
    }

    if (searchTerm) {
      filtered = filtered.filter(record => 
        record.nombre_cliente?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.campana?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.celula?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'todos') {
      filtered = filtered.filter(record => record.status === statusFilter);
    }

    return filtered;
  };

  const filteredRecords = getFilteredRecords();
  const pendientesCount = records.filter(r => r.status === 'pendiente').length;
  const completadosCount = records.filter(r => r.status === 'completado').length;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        <span className="ml-3 text-lg text-gray-600">Cargando registros...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Principal - Estilo exacto como la imagen */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-6 text-white">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-white bg-opacity-20 rounded flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold">📈 Aumento de CPC y Disminución de Tráfico</h1>
              <p className="text-orange-100 mt-1">Gestión de clientes con problemas de aumento de CPC y disminución de tráfico</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">{records.length}</div>
            <div className="text-orange-100">Registros Activos</div>
          </div>
        </div>
      </div>

      {/* Botón Nuevo Registro y Status Legend */}
      <div className="flex justify-between items-center">
        <button
          onClick={() => {
            setEditingRecord(null);
            setShowForm(true);
          }}
          className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center shadow-lg"
        >
          <span className="mr-2">+</span>
          Nuevo Registro
        </button>
        
        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
            <span className="text-sm text-gray-600">Pendiente</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
            <span className="text-sm text-gray-600">En Proceso</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-400 rounded-full"></div>
            <span className="text-sm text-gray-600">Completado</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-400 rounded-full"></div>
            <span className="text-sm text-gray-600">Cancelado</span>
          </div>
        </div>
      </div>

      {/* Tabs - Estilo exacto como la imagen */}
      <div className="flex space-x-8 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('todos')}
          className={`pb-4 px-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'todos'
              ? 'border-orange-500 text-orange-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <span>Todos los Registros</span>
            <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full">{records.length}</span>
          </div>
        </button>
        
        <button
          onClick={() => setActiveTab('pendientes')}
          className={`pb-4 px-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'pendientes'
              ? 'border-orange-500 text-orange-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Pendientes</span>
            <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">{pendientesCount}</span>
          </div>
        </button>
        
        <button
          onClick={() => setActiveTab('completados')}
          className={`pb-4 px-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'completados'
              ? 'border-orange-500 text-orange-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Completados</span>
            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">{completadosCount}</span>
          </div>
        </button>
      </div>

      {/* Filtros - Estilo exacto como la imagen */}
      <div className="flex space-x-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Buscar por cliente, campaña o célula..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-500"
          />
        </div>
        <div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-700"
          >
            <option value="todos">Todos los estados</option>
            <option value="pendiente">Pendiente</option>
            <option value="en_proceso">En Proceso</option>
            <option value="completado">Completado</option>
            <option value="cancelado">Cancelado</option>
          </select>
        </div>
      </div>

      {/* Lista de registros */}
      <CpcTrafficClientsList
        records={filteredRecords}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onShowHistory={handleShowHistory}
        onQuickAction={handleQuickAction}
        currentUser={currentUser}
      />

      {/* Modales */}
      {showForm && (
        <CpcTrafficClientsForm
          record={editingRecord}
          onSave={handleSave}
          onClose={() => {
            setShowForm(false);
            setEditingRecord(null);
          }}
          currentUser={currentUser}
        />
      )}

      {showHistory && selectedRecord && (
        <CpcTrafficClientsHistory
          recordId={selectedRecord.id}
          onClose={() => {
            setShowHistory(false);
            setSelectedRecord(null);
          }}
        />
      )}

      {showQuickAction && quickActionRecord && (
        <CpcTrafficClientsQuickActionModal
          record={quickActionRecord}
          onClose={() => {
            setShowQuickAction(false);
            setQuickActionRecord(null);
          }}
          onSave={handleQuickActionSave}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

export default CpcTrafficClientsSection;
